// Sugalarm — keep a focused field visible above the kiosk keyboard (cycle 277).
//
// Both kiosk text-entry paths cover the focused field when it sits in the
// lower half of the 1024×(600|640) stage:
//   - Pi / iPad kiosk → the packaged `OnScreenKeyboard` (position:absolute,
//     bottom-anchored, z-index 400).
//   - Android-tablet kiosk → the native Android IME.
// In both cases the field's container (`.kioskContent`) is `overflow:visible`,
// so `scrollIntoView` treats the field as "already visible" (it IS in the
// viewport — just behind the keyboard) and does nothing.
//
// The fix is the same for both: while a text field is focused, make its
// `.kioskContent` ancestor a scroll container and reserve `insetPx` of bottom
// space (the keyboard's occluded height; 0 for the native IME, which already
// shrinks the web viewport), then scroll the field up to just below the
// content's top edge — always fully clear of a bottom-anchored keyboard. The
// scroll math derives the kiosk's uniform transform scale (cycle 53) from the
// `.kiosk` element so an upscaled iPad stage measures correctly.
//
// `releaseKeyboardLift()` restores the container exactly (saved inline styles)
// when the keyboard hides / focus leaves the field.

const TOP_GAP_PX = 16;

let lifted: HTMLElement | null = null;
let savedOverflowY = "";
let savedPaddingBottom = "";

export function liftFieldAboveKeyboard(
  field: HTMLElement,
  insetPx: number,
): void {
  const sc = field.closest<HTMLElement>(".kioskContent");
  if (!sc) {
    // The field is not inside the scrollable kiosk content — e.g. a modal
    // overlay that owns its own keyboard-aware scrolling (CgmAuthModal,
    // ActiveBolusesModal). Don't perturb `.kioskContent`; just best-effort
    // center it (the modal handles the rest).
    try {
      field.scrollIntoView({ block: "center", behavior: "smooth" });
    } catch {
      /* older webview — best effort */
    }
    return;
  }

  if (lifted !== sc) {
    releaseKeyboardLift();
    lifted = sc;
    savedOverflowY = sc.style.overflowY;
    savedPaddingBottom = sc.style.paddingBottom;
  }

  // Make it a scroll container, and reserve room below so a bottom-edge field
  // has somewhere to scroll up TO (without the inset, content that fits the
  // viewport can't scroll at all). The native-IME path passes 0 — the OS
  // already shortened the viewport, so the content overflows on its own.
  sc.style.overflowY = "auto";
  if (insetPx > 0) {
    sc.style.paddingBottom = `calc(var(--kiosk-nav-safe-area) + ${Math.round(
      insetPx,
    )}px)`;
  }

  // Defer a frame so the new padding/overflow lays out before we measure.
  requestAnimationFrame(() => {
    if (lifted !== sc) return; // released / re-targeted in the meantime
    const kioskEl = sc.closest<HTMLElement>(".kiosk");
    const scale =
      kioskEl && kioskEl.offsetWidth
        ? kioskEl.getBoundingClientRect().width / kioskEl.offsetWidth
        : 1;
    const fRect = field.getBoundingClientRect();
    const scRect = sc.getBoundingClientRect();
    // Field top relative to the scroller's visible top, in content (un-scaled)
    // pixels, plus the current scroll offset.
    const fieldTopContent =
      (fRect.top - scRect.top) / (scale || 1) + sc.scrollTop;
    sc.scrollTop = Math.max(0, fieldTopContent - TOP_GAP_PX);
  });
}

export function releaseKeyboardLift(): void {
  if (!lifted) return;
  lifted.style.overflowY = savedOverflowY;
  lifted.style.paddingBottom = savedPaddingBottom;
  lifted = null;
  savedOverflowY = "";
  savedPaddingBottom = "";
}
